import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { DocumentRecordValidationViewModel } from '../../view-models/document-record-validation-view-model';
import {
	TableColumn,
	TableFilter,
	TablePagination,
	TableSearchEvent,
} from '../../../../../../shared-components/custom-table/custom-table.types';
import { TranslateService } from '../../../../../../services/translate/translate.service';
import { html } from '../../../../../../utils/html-tag-utils';
import { DateLocalePipe } from '../../../../../../services/date-locale.pipe';
import { AppContextService } from '../../../../../../services/app-context.service';

@Component({
	selector: 'eif-documents-upload-factoring-validation-result',
	templateUrl: './documents-upload-validation-result.component.html',
	styleUrls: ['../../documents-upload.scss'],
	standalone: false,
})
export class DocumentsUploadFactoringValidationResultComponent implements OnInit {
	@Input() validationResultRecords: DocumentRecordValidationViewModel[] = [];
	@Input() pagination: TablePagination | null = null;
	@Input() filters: TableFilter[] = [];
	@Input() enableFilters = false;
	@Input() loading = false;
	@Input() showPagination = true;
	@Input() showPageSize = true;
	@Output() searchRequested = new EventEmitter<TableSearchEvent>();

	public tableColumns: TableColumn[] = [];
	private dateLocalePipe: DateLocalePipe;

	constructor(
		private translateService: TranslateService,
		private appContext: AppContextService
	) {
		this.dateLocalePipe = new DateLocalePipe(this.appContext);
	}

	ngOnInit() {
		this.initializeTableColumns();
	}

	private initializeTableColumns() {
		this.tableColumns = [
			{
				field: 'RowNumber',
				flex: 0.75,
				header: 'documents-upload-factoring-validation-result.DocumentRecordsRow',
				render: (row: DocumentRecordValidationViewModel) => String(row.RowNumber || ''),
			},
			{
				field: 'Type',
				flex: 0.75,
				header: 'documents-upload-factoring-validation-result.DocumentRecordsDocType',
				render: (row: DocumentRecordValidationViewModel) => row.Type || '',
			},
			{
				field: 'Number',
				flex: 0.75,
				header: 'documents-upload-factoring-validation-result.DocumentRecordsNumber',
				render: (row: DocumentRecordValidationViewModel) => row.Number || '',
			},
			{
				field: 'TotalAmount',
				flex: 0.75,
				header: 'documents-upload-factoring-validation-result.DocumentRecordsTotalAmount',
				align: 'right',
				render: (row: DocumentRecordValidationViewModel) => {
					return html`<span class="is-bold">${row.Currency || ''} ${row.TotalAmount || ''}</span>`;
				},
			},
			{
				field: 'IssuedDate',
				header: 'documents-upload-factoring-validation-result.DocumentRecordsIssueDate',
				align: 'center',
				render: (row: DocumentRecordValidationViewModel) => {
					if (!row.IssuedDate) return '';
					const date = new Date(row.IssuedDate);
					return isNaN(date.getTime()) ? row.IssuedDate : this.dateLocalePipe.transform(date, 'yyyy/MM/dd');
				},
			},
			{
				field: 'DueDate',
				header: 'documents-upload-factoring-validation-result.DocumentRecordsDueDate',
				align: 'center',
				render: (row: DocumentRecordValidationViewModel) => {
					if (!row.DueDate) return '';
					const date = new Date(row.DueDate);
					return isNaN(date.getTime()) ? row.DueDate : this.dateLocalePipe.transform(date, 'yyyy/MM/dd');
				},
			},
			{
				field: 'BuyerVat',
				header: 'documents-upload-factoring-validation-result.DocumentRecordsBuyerVat',
				render: (row: DocumentRecordValidationViewModel) => {
					const countryCode = row.BuyerCountryCode || '';
					const vat = row.BuyerVat || '';
					return `${countryCode}${vat}`;
				},
			},
			{
				field: 'SupplierVat',
				header: 'documents-upload-factoring-validation-result.DocumentRecordsSupplierVat',
				render: (row: DocumentRecordValidationViewModel) => {
					const countryCode = row.SupplierCountryCode || '';
					const vat = row.SupplierVat || '';
					return `${countryCode}${vat}`;
				},
			},
			{
				field: 'ValidationStatus',
				header: 'documents-upload-factoring-validation-result.ValidationStatus',
				align: 'center',
				render: (row: DocumentRecordValidationViewModel) => {
					const status = row.ValidationStatus || '';
					return html`<span class="tag upload-doc-state-${status}">
						${this.translateService.translate(status, 'list-uploadedDocumentRecordState')}
					</span>`;
				},
			},
			{
				type: 'actions',
				icon: 'fa-solid fa-chevron-down',
				isExpand: true,
				showActions: (row: DocumentRecordValidationViewModel) => row.ValidationErrors?.length > 0,
			},
		];
	}
}
